import React, { useEffect, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon, Play } from 'lucide-react';
import type { Item } from '@/stores/canvasStore';
import { cn } from '@/lib/utils';
import { LoadingImage } from '@/components/ui/loading-image';
import { getLinkUrl } from '@/lib/linkUtils';
import { parseSocialUrl } from '@/lib/socialLinks';
import {
  CAROUSEL_STACK,
  carouselSlides,
  isUsableSocialImage,
  needsPreviewRefresh,
  socialCaption,
  socialMediaRatio,
  socialMediaType,
  type CarouselSlide,
  type SocialCardContent,
} from '@/lib/socialCard';

// Image-first card for Instagram and TikTok links, after mymind: the post's
// own image at its natural ratio, a thin platform-gradient bar on top, a white
// glyph top-left, a play button on reels and videos, one muted caption line.
// Without an image it renders a designed gradient + handle card, never an
// empty box or a stretched logo. Top Creatives (ScrapedCreativeCard) renders
// through it too, adding its score badge and stats row via the slots.

const BRAND = {
  instagram: {
    bar: 'linear-gradient(90deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)',
    fill: 'linear-gradient(135deg, #833ab4 0%, #c13584 35%, #e1306c 60%, #fd1d1d 82%, #fcaf45 100%)',
    name: 'Instagram',
  },
  tiktok: {
    bar: 'linear-gradient(90deg, #25f4ee 0%, #fe2c55 100%)',
    fill: 'linear-gradient(135deg, #25f4ee 0%, #0b0b0f 38%, #0b0b0f 62%, #fe2c55 100%)',
    name: 'TikTok',
  },
  facebook: {
    bar: 'linear-gradient(90deg, #0866ff 0%, #4f9bff 100%)',
    fill: 'linear-gradient(135deg, #0866ff 0%, #1b4fd8 55%, #0b2a7a 100%)',
    name: 'Facebook',
  },
} as const;

type Brand = keyof typeof BRAND;

function InstagramGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5.5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TikTokGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.6 2h-3.3v13.4a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .9.1V9.2a6.3 6.3 0 1 0 5.3 6.2V8.6a7.9 7.9 0 0 0 4.4 1.4V6.7a4.6 4.6 0 0 1-4.4-4.7z" />
    </svg>
  );
}

function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 21.9v-7h2.4l.4-2.9h-2.8v-1.8c0-.8.2-1.4 1.4-1.4h1.5V6.2a19 19 0 0 0-2.2-.1c-2.2 0-3.7 1.3-3.7 3.8V12H8v2.9h2.5v7A10 10 0 1 1 13.5 21.9z" />
    </svg>
  );
}

/** Instagram's carousel mark: two stacked rounded squares. */
function CarouselGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 2h11a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3h-1V8a3 3 0 0 0-3-3H5a3 3 0 0 1 3-3z" opacity="0.85" />
      <rect x="2" y="7" width="15" height="15" rx="3" />
    </svg>
  );
}

const Glyphs: Record<Brand, React.ComponentType<{ className?: string }>> = {
  instagram: InstagramGlyph,
  tiktok: TikTokGlyph,
  facebook: FacebookGlyph,
};

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 100_000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return String(n);
}

const KIND_LABEL = { image: 'Post', carousel: 'Post', video: 'Reel', profile: 'Profile' } as const;

// ── Carousel stack ───────────────────────────────────────────────

const STACK = CAROUSEL_STACK;
const PEEK_WIDTH = STACK.frontWidth * STACK.peekScale;
const PEEK_HEIGHT = STACK.frontHeight * STACK.peekScale;

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const pct = (n: number) => `${(n * 100).toFixed(3)}%`;
const boxStyle = (b: Box): React.CSSProperties => ({ left: pct(b.left), top: pct(b.top), width: pct(b.width), height: pct(b.height) });

/** The front slide's box. On the last slide the stack flips: front on the right. */
function frontBox(flipped: boolean): Box {
  return {
    left: flipped ? 1 - STACK.padX - STACK.frontWidth : STACK.padX,
    top: STACK.padTop,
    width: STACK.frontWidth,
    height: STACK.frontHeight,
  };
}

function peekBox(side: 'left' | 'right'): Box {
  return {
    left: side === 'right' ? 1 - STACK.padX - PEEK_WIDTH : STACK.padX,
    top: STACK.padTop + (STACK.frontHeight - PEEK_HEIGHT) / 2,
    width: PEEK_WIDTH,
    height: PEEK_HEIGHT,
  };
}

/**
 * Where a slide sits, from its offset to the current one: in front, peeking
 * out (the next one, or the previous one on the last slide), or hidden behind
 * the peek, ready to slide in.
 */
function slidePlacement(offset: number, flipped: boolean): { box: Box; z: number; visible: boolean } {
  if (offset === 0) return { box: frontBox(flipped), z: 3, visible: true };
  if (!flipped && offset === 1) return { box: peekBox('right'), z: 2, visible: true };
  if (flipped && offset === -1) return { box: peekBox('left'), z: 2, visible: true };
  return { box: peekBox(offset > 0 ? 'right' : 'left'), z: 1, visible: false };
}

/** Keeps a press on a carousel control from starting a canvas drag, selection or open. */
const stopPress = {
  onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onPointerDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onDoubleClick: (e: React.SyntheticEvent) => e.stopPropagation(),
};

const SLIDE_MOTION = 'transition-[left,top,width,height,opacity] duration-300 ease-out motion-reduce:transition-none';

function CarouselStack({
  slides,
  alt,
  onFirstImageError,
}: {
  slides: CarouselSlide[];
  alt: string;
  onFirstImageError: () => void;
}) {
  const [index, setIndex] = useState(0);
  const last = slides.length - 1;
  const current = Math.min(index, last);
  const flipped = current === last;
  const front = frontBox(flipped);

  const go = (to: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIndex(Math.max(0, Math.min(last, to)));
  };

  const control = 'pointer-events-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity';

  return (
    <div className="absolute inset-0" role="group" aria-roledescription="carousel" aria-label={`${slides.length} slides`} data-testid="social-carousel-stack">
      {slides.map((slide, i) => {
        const offset = i - current;
        // Only the slides one step away can move into view; the rest stay unloaded
        if (Math.abs(offset) > 2) return null;
        const { box, z, visible } = slidePlacement(offset, flipped);
        const isPeek = visible && offset !== 0;
        return (
          <div
            key={i}
            className={cn(
              'absolute overflow-hidden rounded-lg bg-muted',
              SLIDE_MOTION,
              offset === 0 ? 'shadow-[0_10px_28px_-6px_rgba(0,0,0,0.45)]' : 'shadow-[0_6px_18px_-6px_rgba(0,0,0,0.35)]',
              isPeek && 'cursor-pointer',
            )}
            style={{ ...boxStyle(box), zIndex: z, opacity: visible ? 1 : 0 }}
            aria-hidden={offset !== 0}
            data-testid={offset === 0 ? 'carousel-front' : isPeek ? 'carousel-peek' : undefined}
            {...(isPeek ? { ...stopPress, onClick: go(i), role: 'button', 'aria-label': `Show slide ${i + 1}` } : {})}
          >
            <LoadingImage
              src={slide.image}
              alt={offset === 0 ? `${alt}, slide ${i + 1} of ${slides.length}` : ''}
              wrapperClassName="absolute inset-0"
              className="w-full h-full object-cover"
              draggable={false}
              decoding="async"
              onError={i === 0 ? onFirstImageError : undefined}
            />
          </div>
        );
      })}

      {/*
        Over the front slide: counter, play mark, arrows. Clicks fall through to
        open the post. No platform glyph: the gradient bar says Instagram, and
        carousel covers usually carry their own masthead in that corner.
      */}
      <div className={cn('absolute z-[4] pointer-events-none rounded-lg overflow-hidden', SLIDE_MOTION)} style={boxStyle(front)}>
        <span
          className="absolute top-2.5 right-2.5 rounded-full bg-black/45 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium leading-4 text-white tabular-nums"
          data-testid="carousel-counter"
        >
          {current + 1}/{slides.length}
        </span>
        {slides[current]?.video && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-11 h-11 rounded-full bg-black/35 backdrop-blur-md ring-1 ring-white/40 flex items-center justify-center">
              <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" strokeWidth={0} />
            </div>
          </div>
        )}
        {current > 0 && (
          <button
            type="button"
            className={cn('absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/85 text-neutral-900 shadow-md flex items-center justify-center hover:bg-white', control)}
            aria-label="Previous slide"
            {...stopPress}
            onClick={go(current - 1)}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
        {current < last && (
          <button
            type="button"
            className={cn('absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/85 text-neutral-900 shadow-md flex items-center justify-center hover:bg-white', control)}
            aria-label="Next slide"
            {...stopPress}
            onClick={go(current + 1)}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* Dots, centred under the front slide */}
      <div
        className={cn('absolute z-[4] flex items-center justify-center', SLIDE_MOTION)}
        style={boxStyle({ left: front.left, top: front.top + front.height, width: front.width, height: 1 - front.top - front.height })}
      >
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            className="p-[3px]"
            aria-label={`Slide ${i + 1}`}
            aria-current={i === current ? 'true' : undefined}
            {...stopPress}
            onClick={go(i)}
          >
            <span
              className={cn(
                'block h-1.5 rounded-full transition-all duration-300',
                i === current ? 'w-4 bg-foreground/80' : 'w-1.5 bg-foreground/25 hover:bg-foreground/45',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

interface SocialMediaCardProps {
  item: Item;
  /** fill: the canvas, card fills its box. natural: the Wall, media sets its own height. */
  layout?: 'fill' | 'natural';
  /** Upgrade old or pending cards in the background. Off for synced copies. */
  autoRefresh?: boolean;
  /** Open the post on click. The Wall opens its own detail view instead. */
  openOnClick?: boolean;
  /** Own border, rounding and shadow. Off inside a tile that already frames it (Wall, Pinned). */
  framed?: boolean;
  /** Small tiles (Pinned): profiles drop the follower count and recent posts. */
  compact?: boolean;
  /** Card content when it isn't the item's own (Top Creatives maps its fields onto the card). */
  content?: SocialCardContent;
  /** Bottom-left over the media, e.g. the Top Creatives score. */
  badge?: ReactNode;
  /** Bottom-right over the media, shown on hover. */
  actions?: ReactNode;
  /** A slim row under the caption, e.g. views, likes, comments. */
  footer?: ReactNode;
  /** Top-right over the media, left of the media-type mark, e.g. the Top Creative tag. */
  tag?: ReactNode;
  /** Makes the play button on videos a real button (Top Creatives plays the stored video). */
  onPlay?: () => void;
  /** The media image failed to load. Cards that don't auto-refresh use this to refresh themselves. */
  onImageError?: () => void;
}

function brandOf(platform: string | undefined): Brand {
  return platform && platform in BRAND ? (platform as Brand) : 'instagram';
}

export function SocialMediaCard({
  item,
  layout = 'fill',
  autoRefresh = true,
  openOnClick = true,
  framed = layout === 'fill',
  compact = false,
  content: contentOverride,
  badge,
  actions,
  footer,
  tag,
  onPlay,
  onImageError,
}: SocialMediaCardProps) {
  const content = contentOverride ?? ((item.content ?? {}) as SocialCardContent);
  const url = getLinkUrl(content);
  const info = parseSocialUrl(url);
  const brand = brandOf(info?.platform ?? content.platform);
  const Glyph = Glyphs[brand];
  const mediaType = socialMediaType(content);
  const handle = content.authorHandle || info?.handle;
  const caption = socialCaption(content);

  const [imageError, setImageError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  // A new image (after a refresh) gets a fresh chance
  useEffect(() => setImageError(false), [content.image]);

  const refreshMode = autoRefresh ? (imageError ? 'free' : needsPreviewRefresh(content)) : null;
  useEffect(() => {
    if (!refreshMode) return;
    // Loaded lazily: keeps the Supabase client out of the card's import graph
    import('@/lib/linkPreviewRefresh')
      .then(({ scheduleBackgroundRefresh }) => scheduleBackgroundRefresh(item, refreshMode))
      .catch((err) => console.warn('[SocialMediaCard] refresh unavailable:', err));
    // item is read fresh from the store when the job runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, refreshMode]);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isProfile = mediaType === 'profile';
  const hasImage = isUsableSocialImage(content.image) && !imageError;
  // Pinned tiles are too small for a stack: they keep the cover and the carousel mark
  const slides = compact ? [] : carouselSlides(content);
  const ratio = socialMediaRatio(!hasImage ? { ...content, image: null } : slides.length > 0 ? content : { ...content, slides: undefined });

  const playDisc = (
    <div className="w-14 h-14 rounded-full bg-black/35 backdrop-blur-md ring-1 ring-white/40 flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-105">
      <Play className="w-6 h-6 text-white ml-0.5" fill="currentColor" strokeWidth={0} />
    </div>
  );
  const playButton = mediaType === 'video' && (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="social-play">
      {onPlay ? (
        <button
          type="button"
          className="pointer-events-auto rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label="Play video"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onPlay();
          }}
        >
          {playDisc}
        </button>
      ) : playDisc}
    </div>
  );
  // Clears the carousel or image mark when there is one (the mark sits at top-3 right-3, 18px)
  const hasTypeMark = hasImage && !isProfile && (mediaType === 'carousel' || mediaType === 'image');
  const topRight = tag ? (
    <div className={cn('absolute top-2.5 z-10', hasTypeMark ? 'right-10' : 'right-2.5')} data-testid="social-tag">{tag}</div>
  ) : null;

  let media: React.ReactNode;
  if (isProfile && hasImage) {
    const avatar = content.authorAvatar && !avatarError ? content.authorAvatar : content.image!;
    const recent = compact ? [] : (content.recentImages ?? []).slice(0, 3);
    media = (
      <div className="absolute inset-0 flex flex-col" data-testid="social-profile">
        <img src={content.image!} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-70 dark:opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/25 to-black/45" />
        <div className={cn('relative flex-1 min-h-0 flex flex-col items-center justify-center px-4 text-center', compact ? 'gap-1' : 'gap-2')}>
          <div className="rounded-full p-[3px] shadow-xl" style={{ background: BRAND[brand].fill }}>
            <div className="rounded-full p-[3px] bg-card">
              <img
                src={avatar}
                alt={handle ? `@${handle}` : ''}
                className={cn('rounded-full object-cover', compact ? 'w-12 h-12' : 'w-20 h-20')}
                onError={() => (avatar === content.image ? setImageError(true) : setAvatarError(true))}
              />
            </div>
          </div>
          {content.authorName && (
            <p className="text-white font-semibold text-base leading-tight line-clamp-1 drop-shadow">{content.authorName}</p>
          )}
          {handle && <p className="text-white/85 text-xs font-medium">@{handle}</p>}
          {!!content.authorFollowers && !compact && (
            <p className="text-white/70 text-[11px]">{formatCount(content.authorFollowers)} followers</p>
          )}
        </div>
        {recent.length > 0 && (
          <div className="relative grid grid-cols-3 gap-0.5 px-3 pb-3">
            {recent.map((src) => (
              <img key={src} src={src} alt="" className="w-full aspect-square object-cover rounded-md" />
            ))}
          </div>
        )}
      </div>
    );
  } else if (hasImage && slides.length > 0) {
    media = (
      <CarouselStack
        slides={slides}
        alt={`${BRAND[brand].name} post`}
        onFirstImageError={() => {
          setImageError(true);
          onImageError?.();
        }}
      />
    );
  } else if (hasImage) {
    media = (
      <>
        <LoadingImage
          src={content.image!}
          alt={`${BRAND[brand].name} ${KIND_LABEL[mediaType].toLowerCase()}`}
          wrapperClassName="absolute inset-0"
          className="w-full h-full object-cover"
          draggable={false}
          onError={() => {
            setImageError(true);
            onImageError?.();
          }}
        />
        {/* Keeps the white glyphs legible on light images */}
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
        <Glyph className="absolute top-3 left-3 w-5 h-5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]" />
        {topRight}
        {mediaType === 'carousel' && (
          <span className="absolute top-3 right-3" data-testid="social-carousel">
            <CarouselGlyph className="w-[18px] h-[18px] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]" />
          </span>
        )}
        {mediaType === 'image' && (
          <ImageIcon className="absolute top-3 right-3 w-[18px] h-[18px] text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]" strokeWidth={2} aria-hidden="true" />
        )}
        {playButton}
      </>
    );
  } else {
    // Designed fallback: platform gradient + handle. Videos keep the play
    // button in the centre and move the handle to the bottom.
    const isVideo = mediaType === 'video';
    const label = brand === 'tiktok' && isVideo ? 'Video' : KIND_LABEL[mediaType];
    media = (
      <div className="absolute inset-0" style={{ background: BRAND[brand].fill }} data-testid="social-fallback">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2),transparent_55%)]" />
        <div
          className={cn(
            'absolute flex flex-col gap-2',
            isVideo ? 'inset-x-0 bottom-0 p-4 items-start text-left' : 'inset-0 items-center justify-center px-6 text-center',
            // Clear the badge row (Top Creatives score) along the bottom edge
            isVideo && badge && 'pb-14',
          )}
        >
          <Glyph className={cn('text-white/95', isVideo ? 'w-6 h-6' : 'w-10 h-10')} />
          <p className="text-white font-semibold text-[15px] leading-tight break-all line-clamp-2">
            {handle ? `@${handle}` : BRAND[brand].name}
          </p>
          <p className="text-white/75 text-[11px] font-medium uppercase tracking-[0.12em]">{label}</p>
        </div>
        {topRight}
        {playButton}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full flex flex-col overflow-hidden bg-card cursor-pointer group',
        layout === 'fill' && 'h-full',
        framed && 'rounded-xl border border-border/50 card-shadow transition-colors hover:border-primary/40',
      )}
      onClick={openOnClick ? handleOpen : undefined}
      data-testid="social-card"
      data-media-type={mediaType}
      data-platform={brand}
    >
      <div className="h-[3px] shrink-0" style={{ background: BRAND[brand].bar }} data-testid="social-bar" />
      <div
        className={cn('relative overflow-hidden bg-muted', layout === 'fill' ? 'flex-1 min-h-0' : 'w-full')}
        style={layout === 'natural' ? { aspectRatio: String(ratio) } : undefined}
      >
        {media}
        {badge && <div className="absolute bottom-3 left-3 z-10">{badge}</div>}
        {actions && (
          <div className="absolute bottom-3 right-3 z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            {actions}
          </div>
        )}
      </div>
      <div className="h-9 shrink-0 px-3 flex items-center min-w-0">
        <p className="truncate text-[13px] leading-none text-muted-foreground" title={caption}>
          {caption}
        </p>
      </div>
      {footer && <div className="h-7 shrink-0 px-3 -mt-1.5 pb-1 flex items-center min-w-0">{footer}</div>}
    </div>
  );
}
